<template>
    <div
        :class="containerLayout"
        class="question-content custom-radio-box-container -mx-2 w-full"
    >
        <label
            v-for="(option, i) in options"
            :key="`radio-${i}`"
            :aria-checked="isSelected(option)"
            :aria-label="option[labelField]"
            :class="{
                'px-2 pr-0 bg-transparent': !hasCardStyleModifier,
                checked: isSelected(option),
                'w-full': fullWidth && !hasCardStyleModifier,
                'w-1/2': !fullWidth && !hasCardStyleModifier,
                'rounded-xl ring-2 ring-gray-700 bg-modifier-selectedcard shadow-modifier':
                    isSelected(option) && hasCardStyleModifier,
                'rounded-xl ring-1 ring-gray-200 bg-modifier-card shadow-modifier':
                    !isSelected(option) && hasCardStyleModifier
            }"
            :for="generateInputId(option, i)"
            class="custom-radio-box relative block mb-1 w-full rounded cursor-pointer select-none"
            role="radio"
            tabindex="0"
        >
            <input
                v-if="!visualMenu"
                :id="generateInputId(option, i)"
                :aria-disabled="isUnavailable(option)"
                :aria-label="option[labelField]"
                :checked="isSelected(option)"
                :disabled="isUnavailable(option)"
                :name="generateInputId(option, i)"
                :value="option[`${valueField}`]"
                class="custom-control-input form-check-input absolute opacity-0"
                type="radio"
                @change="onChange(option)"
            />
            <div
                v-if="hasCardStyleModifier"
                class="p-3 pb-6 relative flex gap-0 items-center h-full justify-between flex-col"
            >
                <button
                    v-if="hasAllergens(option) || option.description"
                    class="absolute top-[5px] right-[5px] hover:drop-shadow"
                    @click.stop="
                        $modal.show('modifier-extra-info-modal', {
                            item: option.name,
                            text: getExtraInfoText(option)
                        })
                    "
                >
                    <info-icon class="size-4" />
                </button>
                <div
                    :class="{
                        'pr-2': hasAllergens(option) || option.description
                    }"
                    class="flex w-full"
                >
                    <header
                        class="line-clamp-2 w-full text-xs sm:text-sm text-primary text-center break-words"
                    >
                        {{ option[labelField] }}
                    </header>
                </div>
                <div class="size-[95px]">
                    <img
                        v-if="option.modifierImage"
                        :alt="option[labelField]"
                        :src="getOptimizedImageSrc(option.modifierImage)"
                        :srcset="getOptimizedImageSrcset(option.modifierImage)"
                        class="size-full object-contain transition-all duration-150 ease-in-out"
                        decoding="async"
                        loading="lazy"
                    />
                </div>
                <p
                    v-show="
                        option.price && (showPrices || isPaidOption(option))
                    "
                    class="absolute inset-x-0 pb-1 text-center bottom-0 text-price text-xs"
                >
                    <span v-if="option.price > 0">{{ pricePrefix }}</span>
                    {{ (option.price / 100) | customCurrency }}
                    <s v-if="option.compareAtPrice" class="ml-1 text-gray-400">
                        {{ pricePrefix
                        }}{{ (option.compareAtPrice / 100) | customCurrency }}
                    </s>
                </p>
            </div>
            <div
                v-else
                :class="{ 'cursor-default': visualMenu }"
                class="flex p-2 px-0 items-center"
            >
                <span
                    v-if="!visualMenu && !option.modifierImage"
                    class="custom-radio-indicator"
                />

                <modifier-image
                    v-if="option.modifierImage"
                    :alt="option[labelField]"
                    :image="option.modifierImage"
                    :is-selected="isSelected(option)"
                />
                <div class="custom-radio-box-align">
                    <div class="pl-2 text-itembody text-sm">
                        {{ option[labelField] }}
                        <div class="flex">
                            <span
                                v-if="hasCalories(option)"
                                class="block whitespace-pre-line text-sm text-muted mr-1"
                            >
                                {{ option.nutrition.calories }} kcal
                            </span>
                            <span
                                v-if="
                                    !isUnavailable(option) &&
                                    hasModifierAllergenDisplay &&
                                    hasAllergens(option)
                                "
                                class="block whitespace-pre-line text-sm text-muted mr-1"
                            >
                                {{ getAllergenText(option) }}
                            </span>
                            <span class="text-red-600">
                                {{
                                    isUnavailable(option)
                                        ? $t('radioButton.currentlyUnavailable')
                                        : ''
                                }}
                            </span>
                            <span
                                v-if="showPopularBadge(option)"
                                class="flex items-center text-orange-400 text-xs"
                            >
                                <star-icon class="mr-0.5 w-4 h-4" />
                                {{ $t('menuItem.popular') }}
                            </span>
                        </div>
                    </div>

                    <span v-if="option.compareAtPrice">
                        <p
                            v-show="
                                option.price &&
                                (showPrices || isPaidOption(option))
                            "
                            class="shrink-0 pl-2 whitespace-nowrap text-right text-price text-sm"
                        >
                            {{ pricePrefix
                            }}{{ (option.price / 100) | customCurrency }}

                            <s class="ml-1 text-gray-400">
                                {{ pricePrefix
                                }}{{
                                    (option.compareAtPrice / 100)
                                        | customCurrency
                                }}
                            </s>
                        </p>
                    </span>
                    <span v-else>
                        <p
                            v-show="
                                option.price &&
                                (showPrices || isPaidOption(option))
                            "
                            class="shrink-0 pl-2 whitespace-nowrap text-right text-price text-sm"
                        >
                            <span v-if="option.price > 0">
                                {{ pricePrefix }}
                            </span>
                            {{ (option.price / 100) | customCurrency }}
                        </p>
                    </span>
                </div>
            </div>
        </label>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import isMobile from '@/helpers/isMobileDevice';
import InfoIcon from '@/components/icons/InfoIcon';
import StarIcon from '@/components/icons/StarIcon';
import ModifierImage from '@/components/ModifierImage';
import { processItemAllergens } from '@/helpers/allergens';

export default {
    name: 'RadioButton',
    props: {
        options: Array,
        value: Object,
        keyId: {
            type: String,
            required: false,
            default: 'keyId'
        },
        labelField: {
            type: String,
            required: false,
            default: 'label'
        },
        valueField: {
            type: String,
            required: false,
            default: 'value'
        },
        fullWidth: {
            type: Boolean,
            required: false,
            default: false
        },
        itemPrice: {
            type: Number,
            required: false
        },
        multiMax: {
            type: Number,
            required: false,
            default: 1
        },
        showPrices: {
            type: Boolean,
            required: false,
            default: true
        },
        paidOptionIds: {
            type: Array,
            required: false,
            default: () => []
        }
    },
    components: {
        ModifierImage,
        InfoIcon,
        StarIcon
    },
    methods: {
        onChange(option) {
            this.selectedOption = {
                id: option[this.valueField],
                quantity: 1,
                subModifierGroups: option['subModifierGroups']
            };
        },
        isSelected(option) {
            return (
                this.selectedOption?.[this.valueField] ===
                option[this.valueField]
            );
        },
        generateInputId(option, index) {
            return `${option.id}-${index}-${this.keyId}`;
        },
        hasAllergens(option) {
            const allergenInfo = processItemAllergens(
                option,
                [],
                this.$t('allergens')
            );
            return (
                allergenInfo.allergens.length > 0 ||
                allergenInfo.possibleAllergens.length > 0
            );
        },
        getAllergenText(option) {
            const { allergens, possibleAllergens } = processItemAllergens(
                option,
                [],
                this.$t('allergens')
            );

            const allergenText = [];

            if (allergens.length > 0) {
                allergenText.push(
                    `Contains allergens: ${allergens.join(', ')}`
                );
            }

            if (possibleAllergens.length > 0) {
                allergenText.push(
                    `May contain allergens: ${possibleAllergens.join(', ')}`
                );
            }

            return allergenText.join(' • ');
        },
        getExtraInfoText(option) {
            const allergenText = this.getAllergenText(option);
            const { description = '' } = option;

            if (description && allergenText) {
                return `${description}\n\n${allergenText}`;
            }

            return description || allergenText;
        },
        getOptimizedImageSrc(imageUrl) {
            return `${imageUrl}/-/format/auto/`;
        },
        getOptimizedImageSrcset(imageUrl) {
            return `${imageUrl}/-/format/auto/-/resize/128x/ 1x, ${imageUrl}/-/format/auto/-/resize/192x/ 2x, ${imageUrl}/-/format/auto/-/resize/256x/ 3x`;
        },
        isPaidOption(option) {
            const id = option[this.valueField];
            return this.paidOptionIds.includes(id);
        }
    },
    computed: {
        ...mapState({
            visualMenu: state => state.venue.venue.visualMenu
        }),
        selectedOption: {
            get() {
                return this.value;
            },
            set(newValue) {
                this.$emit('input', newValue);
            }
        },
        isUnavailable() {
            return option => option.snooze || option.isAvailable === false;
        },
        hasCalories() {
            return option => option.nutrition && option.nutrition.calories;
        },
        showPopularBadge() {
            return option => option.popular && option.isAvailable;
        },
        isMobile() {
            return isMobile();
        },
        containerLayout() {
            if (this.hasCardStyleModifier) {
                return `grid gap-3 auto-rows-fr grid-cols-3 sm:grid-cols-4`;
            }

            const baseFlexClasses =
                'space-between flex flex-nowrap items-start';

            if (this.fullWidth)
                return { [`${baseFlexClasses} flex-col`]: true };

            return {
                [`${baseFlexClasses} flex-col`]: this.options.length > 2,
                [`${baseFlexClasses} flex-row`]: this.options.length < 3
            };
        },
        pricePrefix() {
            return this.itemPrice === 0 ? '' : '+';
        },
        hasModifierAllergenDisplay() {
            return this.$growthbook.isOn('modifier_allergens');
        },
        hasCardStyleModifier() {
            const modifierStyle = this.$store.state.venue.venue.modifierStyle;

            if (modifierStyle === 'all_cards') {
                return true;
            }

            if (modifierStyle === 'cards_all_images') {
                return (
                    this.options?.length > 0 &&
                    this.options.every(option => option.modifierImage)
                );
            }

            return false;
        }
    }
};
</script>

<style src="@/assets/css/radio-button.css"></style>
